import { useMemo, useState } from 'react';
import { Alert, App, Button, Card, Select, Space, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PencilSimple, Plus, WarningCircle } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageBody, PageHeader } from '../../app/AppLayout';
import { DataTable } from '../../components/DataTable';
import { HintBanner, HintToggle, useDismissibleHint } from '../../components/DismissibleHint';
import { ErrorState, LoadingState } from '../../components/PageStates';
import { StatusTag } from '../../components/StatusTag';
import { ApiError, NetworkError } from '../../api/client';
import { catalogApi, catalogKeys } from '../catalog/api';
import { tokens } from '../../theme';
import {
  daysLabel,
  reservationSlotApi,
  reservationSlotKeys,
  slotHours,
  type ReservationSlot,
  type UpsertReservationSlotPayload,
} from './api';
import { ReservationSlotFormModal } from './ReservationSlotFormModal';

export function ReservationSlotsPage() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const [branchFilter, setBranchFilter] = useState<string | undefined>();
  const [editing, setEditing] = useState<ReservationSlot | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const helpHint = useDismissibleHint('reservation-slots-intro');

  const listQuery = useQuery({
    queryKey: reservationSlotKeys.list(branchFilter),
    queryFn: () => reservationSlotApi.list(branchFilter),
  });
  const coverageQuery = useQuery({
    queryKey: reservationSlotKeys.coverage,
    queryFn: reservationSlotApi.branchesWithoutSlots,
  });
  const branchesQuery = useQuery({
    queryKey: catalogKeys.branches,
    queryFn: catalogApi.listBranches,
  });

  // Memoize để `?? []` không tạo mảng mới mỗi render (làm `useMemo` bên dưới đổi dep liên tục).
  const branches = useMemo(() => branchesQuery.data ?? [], [branchesQuery.data]);
  const branchName = useMemo(() => {
    const map = new Map(branches.map((b) => [b.id, b.name]));
    return (id: string) => map.get(id) ?? '—';
  }, [branches]);

  const reportError = (error: unknown, fallback: string) => {
    message.error(
      error instanceof ApiError || error instanceof NetworkError ? error.message : fallback,
    );
  };

  const invalidate = async () => {
    // Đổi khung ảnh hưởng cả danh sách lẫn cảnh báo chi nhánh chưa khai — làm mới cả hai.
    await queryClient.invalidateQueries({ queryKey: reservationSlotKeys.all });
  };

  const saveMutation = useMutation({
    mutationFn: (payload: { id?: string; body: UpsertReservationSlotPayload }) =>
      payload.id
        ? reservationSlotApi.update(payload.id, payload.body)
        : reservationSlotApi.create(payload.body),
    onSuccess: async () => {
      await invalidate();
      setEditing(null);
      setIsCreating(false);
      message.success('Đã lưu khung giờ.');
    },
    onError: (error) => reportError(error, 'Không lưu được khung giờ.'),
  });

  const toggleMutation = useMutation({
    mutationFn: (row: ReservationSlot) =>
      row.isActive ? reservationSlotApi.deactivate(row.id) : reservationSlotApi.activate(row.id),
    onSuccess: async (saved) => {
      await invalidate();
      message.success(saved.isActive ? 'Đã bật lại khung giờ.' : 'Đã tắt khung giờ.');
    },
    onError: (error) => reportError(error, 'Không đổi được trạng thái khung giờ.'),
  });

  const busy = toggleMutation.isPending;

  const branchesWithoutSlots = coverageQuery.data ?? [];

  const columns: ColumnsType<ReservationSlot> = [
    {
      title: 'Chi nhánh',
      key: 'branch',
      width: 200,
      fixed: 'left',
      render: (_, row) => <span style={{ fontWeight: 600 }}>{branchName(row.branchId)}</span>,
    },
    {
      title: 'Thứ trong tuần',
      key: 'days',
      width: 180,
      render: (_, row) => <span>{daysLabel(row.daysOfWeek)}</span>,
    },
    {
      title: 'Khung giờ',
      key: 'hours',
      width: 170,
      render: (_, row) => {
        const hours = slotHours(row);
        return (
          <Space size={4} wrap>
            <Tag className="mono">{hours.label}</Tag>
            {hours.overnight && <Tag color="warning">qua nửa đêm</Tag>}
          </Space>
        );
      },
    },
    {
      title: 'Số đơn tối đa',
      dataIndex: 'capacity',
      key: 'capacity',
      width: 120,
      render: (capacity: number) => <span className="mono">{capacity}</span>,
    },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 130,
      render: (_, row) =>
        row.isActive ? (
          <StatusTag color="success">Đang nhận</StatusTag>
        ) : (
          <StatusTag color="default">Đã tắt</StatusTag>
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 170,
      fixed: 'right',
      render: (_, row) => (
        <Space size={4} wrap>
          <Button
            size="small"
            icon={<PencilSimple size={14} />}
            aria-label={`Sửa khung ${slotHours(row).label} của ${branchName(row.branchId)}`}
            onClick={() => setEditing(row)}
          >
            Sửa
          </Button>
          <Button
            size="small"
            danger={row.isActive}
            disabled={busy}
            onClick={() => toggleMutation.mutate(row)}
          >
            {row.isActive ? 'Tắt' : 'Bật'}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Khung giờ đặt bàn"
        subtitle="Dữ liệu"
        hint={<HintToggle hint={helpHint} />}
        actions={
          <Button type="primary" icon={<Plus size={16} />} onClick={() => setIsCreating(true)}>
            Thêm khung giờ
          </Button>
        }
      />
      <PageBody>
        {/* Không lặp title của PageHeader ("Khung giờ đặt bàn") — chỉ giữ câu dẫn giải thích. */}
        <Typography.Paragraph
          type="secondary"
          style={{ marginTop: 0, marginBottom: tokens.space.sectionGap }}
        >
          Mỗi khung khai <b>thứ</b> áp dụng, <b>khoảng giờ</b> và <b>số đơn tối đa</b> mỗi ngày. Chi
          nhánh chưa khai khung nào thì <b>không nhận được đơn đặt bàn nào</b>.
        </Typography.Paragraph>

        <HintBanner
          hint={helpHint}
          gap={16}
          message="Hai khung cùng chi nhánh không được trùng giờ"
          description="Nếu hai khung phủ chung một khoảng giờ trên các thứ chung, server sẽ chặn và nói rõ khung nào chồng khung nào. Tắt một khung thay vì xóa — đơn đã nhận vẫn cần khung làm căn cứ."
        />

        {/*
          Cảnh báo nghiệp vụ — LUÔN hiện khi còn chi nhánh chưa khai slot, không phải hint đóng được:
          chi nhánh không có khung giờ thì khách và lễ tân bị chặn mà không ai biết vì sao.
        */}
        {branchesWithoutSlots.length > 0 && (
          <Alert
            type="warning"
            showIcon
            icon={<WarningCircle size={20} weight="fill" aria-hidden />}
            style={{ marginBottom: 16 }}
            message={`${branchesWithoutSlots.length} chi nhánh chưa khai khung giờ nào`}
            description={
              <>
                <div style={{ marginBottom: 6 }}>
                  Những chi nhánh này đang mở nhưng <b>không nhận được đơn đặt bàn nào</b> tới khi khai
                  ít nhất một khung giờ:
                </div>
                <Space size={4} wrap>
                  {branchesWithoutSlots.map((b) => (
                    <Tag key={b.branchId} color="warning">
                      {b.branchName}
                    </Tag>
                  ))}
                </Space>
              </>
            }
          />
        )}

        {listQuery.isPending && <LoadingState />}
        {listQuery.isError && (
          <ErrorState error={listQuery.error} onRetry={() => void listQuery.refetch()} />
        )}
        {listQuery.isSuccess && (
          <Card>
            <DataTable
              rowKey="id"
              size="small"
              columns={columns}
              dataSource={listQuery.data}
              onReload={() => void listQuery.refetch()}
              searchable={false}
              pagination={{ pageSize: 20, showSizeChanger: false }}
              scroll={{ x: 960 }}
              locale={{
                emptyText: branchFilter
                  ? 'Chi nhánh này chưa khai khung giờ nào.'
                  : 'Chưa có khung giờ nào. Khách chưa đặt bàn được tới khi khai khung đầu tiên.',
              }}
              toolbarExtra={
                <Select
                  allowClear
                  showSearch
                  size="small"
                  optionFilterProp="label"
                  placeholder="Chi nhánh: tất cả"
                  style={{ minWidth: 220 }}
                  value={branchFilter}
                  onChange={setBranchFilter}
                  options={branches.map((b) => ({ value: b.id, label: b.name }))}
                  aria-label="Lọc theo chi nhánh"
                />
              }
            />
          </Card>
        )}
      </PageBody>

      <ReservationSlotFormModal
        slot={editing}
        isCreating={isCreating}
        branches={branches}
        submitting={saveMutation.isPending}
        onClose={() => {
          setEditing(null);
          setIsCreating(false);
        }}
        onSubmit={(body) => {
          if (isCreating) saveMutation.mutate({ body });
          else if (editing) saveMutation.mutate({ id: editing.id, body });
        }}
      />
    </>
  );
}
